/**
 * Showing money, dates and numbers.
 *
 * Formatting only. A formatted string never goes back into a calculation - every
 * sum happens on the integer agorot.
 */
import { formatMinor, toArabicDigits, type Currency, type Lang } from '@pos/shared';
import type { StringKey } from './i18n/en.js';

export interface FormatOpts {
  lang: Lang;
  arabicDigits: boolean;
  currencies: Currency[];
}

const digits = (s: string, arabic: boolean) => (arabic ? toArabicDigits(s) : s);

function group(n: string): string {
  const [w, f] = n.split('.');
  return w.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (f ? `.${f}` : '');
}

/** 12345 agorot -> "₪123.45" */
export function money(agorot: number, o: FormatOpts, code = 'ILS'): string {
  const cur = o.currencies.find((c) => c.code === code);
  const decimals = cur?.decimals ?? 2;
  const symbol = cur?.symbol ?? '₪';
  const neg = agorot < 0;
  const body = group(formatMinor(Math.abs(Math.round(agorot)), decimals));
  // Wrapped as one left-to-right run: in Arabic text a bare "₪12.50" can be
  // laid out as "12.50₪" on one line and "₪12.50" on the next, depending on
  // what sits beside it. The marks are invisible and stop that.
  return `\u2066${neg ? '-' : ''}${symbol}${digits(body, o.arabicDigits)}\u2069`;
}

/** Without the symbol, for a column that already has one in its heading. */
export function amount(agorot: number, o: FormatOpts, code = 'ILS'): string {
  const decimals = o.currencies.find((c) => c.code === code)?.decimals ?? 2;
  return digits(group(formatMinor(Math.round(agorot), decimals)), o.arabicDigits);
}

export function num(n: number, o: FormatOpts): string {
  return digits(group(String(n)), o.arabicDigits);
}

export function percent(n: number, o: FormatOpts): string {
  return `${digits(String(Math.round(n * 10) / 10), o.arabicDigits)}%`;
}

const LOCALE: Record<Lang, string> = { en: 'en-GB', ar: 'ar-EG' };

export function date(iso: string | Date, o: FormatOpts): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  // Arabic month names as they are said here (أيلول, not سبتمبر), with Western
  // digits; the shop's own Arabic-digits setting still applies below.
  const s = d.toLocaleDateString(o.lang === 'ar' ? 'ar-PS-u-nu-latn' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  return o.arabicDigits ? toArabicDigits(s) : s;
}

export function time(iso: string | Date, o: FormatOpts): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const s = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return o.arabicDigits ? toArabicDigits(s) : s;
}

export function dateTime(iso: string | Date, o: FormatOpts): string {
  return `${date(iso, o)} ${time(iso, o)}`;
}

export function relativeDays(iso: string, lang: Lang): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return lang === 'ar' ? 'اليوم' : 'today';
  if (days === 1) return lang === 'ar' ? 'أمس' : 'yesterday';
  return lang === 'ar' ? `من ${days} يوم` : `${days} days ago`;
}

/** Local calendar day, not UTC - a shop reads its reports in shop time. */
export function dayStart(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
export function dayEnd(d: Date): Date {
  const x = dayStart(d);
  x.setDate(x.getDate() + 1);
  return x;
}

export type RangeKey = 'today' | 'yesterday' | 'week' | 'month' | 'lastMonth' | 'year' | 'custom';

export function presetRange(key: RangeKey): { from: string; to: string } {
  const now = new Date();
  switch (key) {
    case 'today':
      return { from: dayStart(now).toISOString(), to: dayEnd(now).toISOString() };
    case 'yesterday': {
      const y = new Date(now.getTime() - 86_400_000);
      return { from: dayStart(y).toISOString(), to: dayEnd(y).toISOString() };
    }
    case 'week': {
      const start = dayStart(now);
      // The trading week here starts on Monday.
      const dow = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - dow);
      return { from: start.toISOString(), to: dayEnd(now).toISOString() };
    }
    case 'month':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), to: dayEnd(now).toISOString() };
    case 'lastMonth':
      return {
        from: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString(),
        to: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
      };
    case 'year':
      return { from: new Date(now.getFullYear(), 0, 1).toISOString(), to: dayEnd(now).toISOString() };
    default:
      return { from: dayStart(now).toISOString(), to: dayEnd(now).toISOString() };
  }
}

export function inputDate(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * The name to show for how something was paid.
 *
 * There are five methods - cash, card, store credit, on account and a custom
 * one the owner named. Every place that needed this had spelled it out as a
 * chain of two questions, "is it cash, is it card, otherwise call it store
 * credit", so BOTH "on account" and any custom method printed on the receipt as
 * "Store credit" - which says the shop owes the customer when in fact the
 * customer owes the shop. Exactly backwards, on a piece of paper handed to
 * them. Written once here so the four screens that print it cannot drift again.
 */
export function paymentLabel(
  method: string,
  t: (key: StringKey) => string,
  methodLabel?: string | null,
): string {
  switch (method) {
    case 'cash': return t('pay.cash');
    case 'card': return t('pay.card');
    case 'credit': return t('pay.credit');
    case 'gift': return t('pay.gift');
    case 'tab': return t('pay.tab');
    // The owner's own name for it ("Jawwal Pay") beats any label we could pick.
    case 'other': return methodLabel || t('pay.other');
    default: return methodLabel || method;
  }
}
